/* eslint-disable @atlaskit/design-system/ensure-design-token-usage */
/* eslint-disable jira/react/no-style-attribute */
/* eslint-disable @atlaskit/ui-styling-standard/enforce-style-prop */
import React, { useEffect, useRef, useState } from 'react';
// eslint-disable-next-line jira/restricted/react-18
import { createRoot } from 'react-dom/client';
import { JiraContextContainer } from '@atlassian/jira-context-container/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { createRouterSelector } from '@atlassian/react-resource-router';

const useRouterLocation = createRouterSelector((state) => state.location);
const useRouterAction = createRouterSelector((state) => state.action);
const useRouterQuery = createRouterSelector((state) => state.query);
const useRouterRoute = createRouterSelector((state) => state.route);
const useRouterMatch = createRouterSelector((state) => state.match);

const RouteComponentWithRouterProps = () => {
	const location = useRouterLocation();
	const action = useRouterAction();
	const query = useRouterQuery();
	const route = useRouterRoute();
	const match = useRouterMatch();

	const { component: RouteComponent } = route;
	return (
		RouteComponent && (
			<RouteComponent
				location={location}
				route={route}
				match={match}
				action={action}
				query={query}
			/>
		)
	);
};

export function ConcurrentRootComponent() {
	const containerRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		if (!containerRef.current) {
			// this is actually "impossible"
			throw new Error('ref is not set');
		}
		const root = createRoot(containerRef.current);
		root.render(
			<JiraContextContainer>
				<RouteComponentWithRouterProps />
				{fg('concurrent-rendering-change-advisory') ? <ConcurrentRouteAdvisor /> : null}
			</JiraContextContainer>,
		);

		return () => {
			// We need the setTimeout to avoid this error:
			// `Attempted to synchronously unmount a root while React was already rendering. React cannot finish unmounting the root until the current render has completed, which may lead to a race condition.`
			setTimeout(() => {
				root.unmount();
			});
		};
	}, []);

	return <div id="concurrent-route-component" ref={containerRef} />;
}

function ConcurrentRouteAdvisor() {
	const [isAdvisoryVisible, setIsAdvisoryVisible] = useState(true);

	useEffect(() => {
		// eslint-disable-next-line no-console
		console.log(
			'%c‼️ This route is being rendered with React concurrent rendering ‼️\nIf you encounter any problems, report them to https://atlassian.enterprise.slack.com/archives/C06176WU6PQ',
			'color: white; background: darkred; font-size: 16px; padding: 4px',
		);

		const timer = setTimeout(() => {
			setIsAdvisoryVisible(false);
		}, 5000);

		return () => {
			clearTimeout(timer);
		};
	}, []);

	return (
		<div
			style={{
				position: 'absolute',
				left: 8,
				top: 12,
				display: 'flex',
				alignItems: 'center',
				// By giving this element a height of 0 and no pointer events, we ensure that it doesn't interfere with the page.
				// To make is visible again, we then set the overflow to "visible".
				height: 0,
				overflow: 'visible',
				pointerEvents: 'none',
			}}
		>
			<div
				style={{
					backgroundColor: isAdvisoryVisible ? 'white' : 'red',
					borderRadius: '50%',
					width: 8,
					height: 8,
					zIndex: 10,
				}}
			/>

			{isAdvisoryVisible && (
				<div
					style={{
						backgroundColor: 'red',
						color: 'white',
						height: '1.5em',
						marginLeft: -12,
						paddingLeft: 16,
						paddingRight: 8,
						borderRadius: 4,
					}}
					// eslint-disable-next-line jira/i18n/text-wrapped-in-format-message
				>
					Rendered in React concurrent mode
				</div>
			)}
		</div>
	);
}
